import { useEffect, useMemo, useRef, useState } from "react";
import { bool, string, number } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { CommentTypes, hashCode } from "@repo/utils";
import { CommentGroupModel } from "@repo/structure";
import { useStores } from "@hooks";
import { useEstimateEditorSettings, useEstimateEditorStore, useEstimateComments } from "@tools";
import { PopMenu, TooltipIcon } from "@components";
import { Badge } from "@material-ui/core";
import { ElementChatPopupContent } from "./ElementChatPopupContent";
import { CommentAdd, Comment, CommentFilled } from "@assets";
import useStyle from "./ElementChatPopup.style";
import classnames from "classnames";

export const ElementChatPopup = observer(({
  path,
  taskName,
  className,
  rowThread,
  hideBadge,
  useMaxHeight,
  maxDisplayReplies,
  scrollToThread=true,
  containerClass,
}) => {
  const { t } = useTranslation();
  const { stateStore } = useStores();
  const { allowCommenting, isProposal } = useEstimateEditorSettings();
  const editorStore = useEstimateEditorStore();
  const commentStore = useEstimateComments();
  const classes = useStyle();
  
  const anchor = useRef(null);
  const { groups, useExternalComments } = commentStore;
  const { currentVersionKey } = editorStore;

  const [ threadVisible, showThread ] = useState(false);
  
  const tmpGroup = useMemo(() => (
    CommentGroupModel.create({
      path,
      version: currentVersionKey,
      name: taskName,
      usedType: isProposal ? CommentTypes.EXTERNAL : CommentTypes.INTERNAL
    })
  ), [path, currentVersionKey, taskName, useExternalComments, isProposal]);
  
  const group = useMemo(() => (
    commentStore.getPathGroup(path, currentVersionKey) || tmpGroup
  ), [path, currentVersionKey, tmpGroup, groups, groups?.length]);
  
  const { hasAnyThreads } = group;

  const childComments = commentStore.getPathChildComments(path, currentVersionKey);

  const noBadge = useMemo(() => (
    hideBadge || !childComments
  ), [ hideBadge, childComments ]);

  const pinIcon = useMemo(() => (
    !(!hasAnyThreads && !(childComments && !hideBadge) && rowThread)
  ), [ hasAnyThreads, childComments, hideBadge, rowThread ]);
  
  useEffect(() => {
    if(anchor.current && group && editorStore.displayedComment && path === (editorStore.displayedComment.path || editorStore.displayedComment.place)) {
      
      if(!editorStore.displayedComment.onlyJumpToThread) {
        showThread(true);
        group.setUsedType(editorStore.displayedComment.internal ? CommentTypes.INTERNAL : CommentTypes.EXTERNAL);
      }
      
      editorStore.setDisplayedComment(null);
      const container = containerClass ? anchor.current.closest(`.${containerClass}`) : anchor.current.closest("[role='row']");
      const scrollRef = stateStore.useAuxiliaryScrollRef ? stateStore.auxiliaryScrollRef : stateStore.scrollRef
      const containerRect = container.getBoundingClientRect();
      const top = Math.round(containerRect.top) + scrollRef.scrollTop + container.offsetHeight - document.body.offsetHeight/2;
      // console.log("removed", top, stateStore.scrollRef.scrollTop);
      // anchor.current.scrollIntoView({ behavior: "smooth", block: "center" })
      
      if(scrollToThread || (top > 0 && top !== scrollRef.scrollTop))
        scrollRef?.scrollTo({ top, behavior: "smooth" });
    }
  }, [editorStore.displayedComment, anchor.current]);
  
  const handleThreadVisibility = () => showThread(!threadVisible);
  
  if(!allowCommenting && !hasAnyThreads)
    return <></>;

  return (
    <>
      <Badge
        color="primary"
        invisible={noBadge}
        badgeContent={childComments}
        overlap="circular"
        max={9}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        data-chat={hashCode(path)}
        className={classnames(pinIcon && "order-first")}
      >
        <TooltipIcon
          ref={anchor}
          color="primary"
          className={classnames(
            "button-comment",
            "noDrag",
            !pinIcon && "hoverIcon opq",
            className
          )}
          onClick={handleThreadVisibility}
          icon={
            hasAnyThreads || (childComments > 0 && !noBadge)
              ? (
                hasAnyThreads === CommentTypes.EXTERNAL
                  ? <CommentFilled />
                  : <Comment />
              )
              : <CommentAdd />
          }
        >
          {t(`views.editor.comments.${allowCommenting ? "tooltip" : "tooltip_readonly"}`)}
        </TooltipIcon>
      </Badge>
      <PopMenu
        id="commentThread"
        pathment="right-start"
        anchor={anchor.current}
        className={classes.root}
        show={threadVisible}
        onClickAway={handleThreadVisibility}
      >
        <ElementChatPopupContent
          group={group}
          path={path}
          taskName={taskName}
          onClose={handleThreadVisibility}
          useMaxHeight={useMaxHeight}
          maxDisplayReplies={maxDisplayReplies}
        />
      </PopMenu>
    </>
  );
});

ElementChatPopup.propTypes = {
  path: string.isRequired,
  taskName: string.isRequired,
  rowThread: bool,
  className: string,
  hideBadge: bool,
  useMaxHeight: bool,
  maxDisplayReplies: number,
  containerClass: string,
};